// One replay run: its progress while it works, then its report (or why it stopped).

import { Button, EmptyState, formatDateTime, PageHeader, Skeleton, toast } from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { ArrowLeft, History, Plus, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { meQueryOptions } from '../../../../auth/session.js';
import { ApiError } from '../../../../lib/api.js';
import {
  ReplayBreadcrumb,
  RunStatusBadge,
  useCanRunReplays,
  useStartReplayRun,
} from '../../../../replay/components.js';
import { rangeLength } from '../../../../replay/labels.js';
import { ProgressView, StoppedView } from '../../../../replay/progress.js';
import {
  cancelRun,
  connectionsQuery,
  isBusy,
  REPLAY_LISTS,
  replayKeys,
  runQuery,
  scenariosQuery,
} from '../../../../replay/queries.js';
import {
  PracticeBranchNote,
  Report,
  usePrintLists,
  VerdictBadge,
} from '../../../../replay/report.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { plainDateLabel, accountToday } from '../../../../stock/labels.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/replay/runs/$runId')({
  validateSearch: z.object({
    /** Which of the report's lists is open. */
    list: z.enum(REPLAY_LISTS).optional().catch(undefined),
  }),
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.runId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(runQuery(params.runId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: RunPage,
  pendingComponent: RunSkeleton,
  errorComponent: RunError,
  notFoundComponent: RunNotFound,
});

function RunPage() {
  const { runId } = Route.useParams();
  const { list } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: run } = useSuspenseQuery(runQuery(runId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const scenarios = useQuery(scenariosQuery());
  const connections = useQuery(connectionsQuery());
  const scenario = scenarios.data?.items.find((s) => s.id === run.scenario_id);
  const sourceName =
    connections.data?.connections.find((c) => c.id === scenario?.connection_id)?.display_name ??
    'the source';
  const canRun = useCanRunReplays();
  const { start, pending: starting } = useStartReplayRun();
  const queryClient = useQueryClient();
  const busy = isBusy(run.status);
  // When the run stops, the scenario list (its "last run") is out of date.
  useEffect(() => {
    if (!busy) void queryClient.invalidateQueries({ queryKey: replayKeys.scenarios() });
  }, [busy, queryClient]);
  const title = scenario?.name ?? 'Sales replay';
  useDocumentTitle(`${title} · Sales replay`);

  // Refused while the run writes its practice branch (`run_busy`): said on the page, and
  // the poll carries on; cleared once the run moves to another step.
  const [refusedAtStep, setRefusedAtStep] = useState<string | null>(null);
  const cancel = useMutation({
    mutationFn: () => cancelRun(run.id),
    onSuccess: (cancelled) => {
      queryClient.setQueryData(replayKeys.run(run.id), cancelled);
      toast.info('Replay cancelled', {
        description: 'What it had written stays in the practice branch.',
      });
      void queryClient.invalidateQueries({ queryKey: replayKeys.scenarios() });
    },
    onError: (error) => {
      const code = error instanceof ApiError && error.status === 409 ? error.code : undefined;
      if (code === 'run_busy') {
        setRefusedAtStep(run.progress.step ?? '');
      } else if (code === 'run_not_active') {
        toast.info('The replay had already finished', {
          description: 'There was nothing left to cancel.',
        });
      } else {
        toast.error('The replay couldn’t be cancelled', {
          description:
            error instanceof ApiError ? error.message : 'Check your connection and try again.',
        });
      }
      void queryClient.invalidateQueries({ queryKey: replayKeys.run(run.id) });
    },
  });
  const cancelRefused = refusedAtStep !== null && refusedAtStep === (run.progress.step ?? '');

  // Print waits for the three printed lists (review UI2A-302); a list that failed is
  // fetched again first, then the page prints (saying so if it failed again).
  const hasReport = run.status === 'completed' && !!run.report;
  const printLists = usePrintLists(run.id, hasReport);
  const [retrying, setRetrying] = useState(false);
  const print = async () => {
    if (printLists.failed) {
      setRetrying(true);
      await printLists.retry();
      setRetrying(false);
      // Let the lists render what came back (or that they failed again) before printing.
      await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    }
    window.print();
  };
  const printWaits = !printLists.ready || retrying;
  // When it was printed, for the printed header (B-032): set as the browser prints, however
  // printing was started.
  const [printedAt, setPrintedAt] = useState(() => new Date());
  useEffect(() => {
    const stamp = () => setPrintedAt(new Date());
    window.addEventListener('beforeprint', stamp);
    return () => window.removeEventListener('beforeprint', stamp);
  }, []);

  const runAgain = () => scenario && start({ id: scenario.id });
  const isLatest = !scenario?.last_run || scenario.last_run.id === run.id;

  return (
    <>
      <PageHeader
        eyebrow={<ReplayBreadcrumb page={title} />}
        title={
          // The state (and a finished run's verdict) beside the name (review UI2A-17).
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span>{title}</span>
            <span
              className="inline-flex flex-wrap items-center gap-1.5 font-body text-base font-normal tracking-normal"
              data-testid="run-badges"
            >
              <RunStatusBadge run={run} />
              <VerdictBadge run={run} />
            </span>
          </span>
        }
        description={
          scenario && (
            <>
              {sourceName} sales of {plainDateLabel(scenario.from, today)} to{' '}
              {plainDateLabel(scenario.to, today)} ({rangeLength(scenario.from, scenario.to)})
              {' · '}
              {scenario.options.production_buffer_pct}% production buffer
            </>
          )
        }
        actions={
          <>
            {hasReport && (
              // The whole report on paper or as a PDF, to share (review UI2A-16).
              <Button
                variant="secondary"
                icon={<Printer aria-hidden />}
                onClick={() => void print()}
                disabled={printWaits}
                loading={printWaits}
                data-testid="replay-print"
                className="print:hidden"
              >
                {printWaits ? 'Loading the lists to print…' : 'Print / save as PDF'}
              </Button>
            )}
            {
              // A finished replay is a record: the way on is a new replay (review CR2-22).
              run.status === 'completed' && canRun && scenario && isLatest ? (
                <Button variant="secondary" asChild className="print:hidden">
                  <Link to="/replay/new" search={{ copy: scenario.id }}>
                    <Plus aria-hidden />
                    Start a new replay
                  </Link>
                </Button>
              ) : !isLatest && scenario?.last_run ? (
                <Button variant="secondary" asChild className="print:hidden">
                  <Link to="/replay/runs/$runId" params={{ runId: scenario.last_run.id }}>
                    <History aria-hidden />
                    Latest run
                  </Link>
                </Button>
              ) : undefined
            }
          </>
        }
      />
      {/* On paper: whose it is, when it was printed, and which run (B-032). */}
      <p
        className="mb-3 hidden border-b border-border pb-2 text-sm text-fg print:block"
        data-testid="replay-print-header"
      >
        {me.account.name} · Printed {formatDateTime(printedAt, { timeZone: me.account.timezone })} ·
        Run {run.id.slice(0, 8)}
      </p>
      <PracticeBranchNote scenario={scenario} />
      {busy ? (
        <ProgressView
          run={run}
          sourceName={sourceName}
          canCancel={canRun}
          cancelling={cancel.isPending}
          cancelRefused={cancelRefused}
          onCancel={() => cancel.mutate()}
        />
      ) : run.status === 'completed' && run.report ? (
        <Report
          run={run}
          report={run.report}
          today={today}
          list={
            list ??
            (run.report.coverage.unmapped > 0
              ? 'unmapped'
              : run.report.vat.mismatched + run.report.vat.errors > 0
                ? 'vat'
                : 'consumption')
          }
          onList={(l) => void navigate({ search: { list: l }, replace: true })}
        />
      ) : (
        <StoppedView
          run={run}
          scenario={scenario}
          canRun={canRun}
          starting={starting}
          onRunAgain={runAgain}
        />
      )}
    </>
  );
}

function RunSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading the replay</h1>
      <span className="sr-only">Loading the replay…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-5 w-96 max-w-full" />
      </div>
      <Skeleton className="mb-6 h-14 w-full rounded-md" />
      <Skeleton className="mb-6 h-32 w-full rounded-lg" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/replay">
        <ArrowLeft aria-hidden />
        Back to Sales replay
      </Link>
    </Button>
  );
}

function RunError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This replay"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function RunNotFound() {
  useDocumentTitle('Replay not found');
  return (
    <>
      <div className="pb-4">
        <ReplayBreadcrumb page="Not found" />
      </div>
      <EmptyState
        icon={History}
        titleAs="h1"
        title="Replay not found"
        description="There’s no replay run at this address. It may be in another account, or the link may be mistyped."
        action={<BackButton />}
      />
    </>
  );
}
